import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink, Ticket, Wine, Store, QrCode } from "lucide-react";
import { SubpageHeader } from "../components/SubpageHeader";
import { Ornament } from "../components/Chapter";
import { useLanguage } from "../i18n/LanguageContext";
import { useSEO, breadcrumbLd } from "../components/SEO";
import { PATROCINADORES } from "../data/parceiros";
import { antesDoFim1aEdicao } from "../data/datas";

const Heading = ({ icon: Icon, children, testid }) => (
    <div className="flex items-center gap-3" data-testid={testid}>
        <Icon size={16} className="text-[#c9a24b] shrink-0" strokeWidth={1.5} />
        <h2 className="font-caps text-xs sm:text-sm tracking-[0.28em] uppercase gold-text">{children}</h2>
    </div>
);

const Protocolo = ({ id, logo, titulo, tag, texto, cta, url, codigo, testid }) => (
    <article id={id} className="scroll-mt-28 border border-[#c9a24b]/20 bg-[#0a0705]/70 p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-start" data-testid={testid}>
        <img loading="lazy" decoding="async" src={logo} alt={titulo} className="h-24 w-auto max-w-[220px] object-contain shadow-[0_10px_30px_-14px_rgba(0,0,0,0.9)] shrink-0" />
        <div className="flex-1">
            <p className="font-caps text-[9px] tracking-[0.3em] uppercase text-[#c9a24b]/70">{tag}</p>
            <h3 className="font-display font-medium text-2xl text-[#efe6d6] mt-1">{titulo}</h3>
            <p className="font-light text-sm sm:text-base text-[#efe6d6]/75 leading-relaxed mt-3">{texto}</p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
                {codigo && (
                    <span className="inline-flex items-center font-caps text-[10px] tracking-[0.25em] uppercase text-[#070503] bg-[#c9a24b] px-3 py-2" data-testid={`${testid}-codigo`}>{codigo}</span>
                )}
                {cta && url && (
                    <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-link`}
                        className="group inline-flex items-center gap-2 font-caps text-[10px] tracking-[0.22em] uppercase text-[#c9a24b] border border-[#c9a24b]/50 px-4 py-2 hover:bg-[#c9a24b] hover:text-[#070503] transition-colors duration-400">
                        {cta} <ExternalLink size={12} className="group-hover:translate-x-0.5 transition-transform duration-300" />
                    </a>
                )}
            </div>
        </div>
    </article>
);

export default function VantagensSocioPage() {
    const { t } = useLanguage();
    const v = t.vantagens;
    const srVinho = PATROCINADORES.find((p) => p.id === "sr-vinho");
    const famosa = PATROCINADORES.find((p) => p.id === "famosa");
    const cr7 = PATROCINADORES.find((p) => p.id === "cr7-helder");
    const comBilhetes = antesDoFim1aEdicao(); // tabela de bilhetes da 1ª edição desaparece a 15/11/2026
    useSEO({
        title: `${v.titulo} · Fados à Mesa`,
        description: v.intro,
        jsonLd: breadcrumbLd([{ name: t.bc.home, path: "/" }, { name: v.label, path: "/socios/vantagens" }]),
    });

    return (
        <div className="min-h-screen bg-[#070503] text-[#efe6d6] relative px-5 sm:px-10 pt-28 pb-24" data-testid="vantagens-page">
            <SubpageHeader />
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="mx-auto w-full max-w-4xl">
                <div className="flex justify-center mb-8"><Ornament className="w-40" /></div>
                <p className="font-caps text-[10px] tracking-[0.35em] uppercase text-[#c9a24b] text-center">{v.label}</p>
                <h1 className="font-caps gold-text text-2xl sm:text-3xl tracking-[0.18em] text-center mt-3" data-testid="vantagens-title">{v.titulo}</h1>
                <p className="font-display italic font-light text-lg sm:text-xl text-[#efe6d6]/70 mt-8 max-w-2xl mx-auto leading-relaxed text-center" data-testid="vantagens-intro">{v.intro}</p>
                <div className="mt-6 flex items-start gap-3 max-w-2xl mx-auto border border-[#c9a24b]/15 px-5 py-4" data-testid="vantagens-prova">
                    <QrCode size={18} className="text-[#c9a24b] shrink-0 mt-0.5" strokeWidth={1.5} />
                    <p className="font-light text-sm text-[#efe6d6]/70 leading-relaxed">{v.prova}</p>
                </div>

                {comBilhetes && (
                    <section className="mt-16" data-testid="vantagens-bilhetes">
                        <Heading icon={Ticket} testid="vantagens-bilhetes-title">{v.bilhetesTitulo}</Heading>
                        <p className="font-light text-sm sm:text-base text-[#efe6d6]/75 leading-relaxed mt-4">{v.bilhetesIntro}</p>
                        <div className="mt-6 overflow-x-auto border border-[#c9a24b]/20">
                            <table className="w-full text-left text-sm" data-testid="vantagens-bilhetes-tabela">
                                <thead>
                                    <tr className="bg-[#c9a24b]/10 font-caps text-[9px] tracking-[0.25em] uppercase text-[#c9a24b]">
                                        <th className="px-4 py-3 font-normal">{v.colBilhete}</th>
                                        <th className="px-4 py-3 font-normal whitespace-nowrap">{v.colAte}</th>
                                        <th className="px-4 py-3 font-normal whitespace-nowrap">{v.colDepois}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {v.linhas.map((l, i) => {
                                        const socio = i === 1 || i === 2 || i === 4;
                                        return (
                                            <tr key={l[0]} className={`border-t border-[#c9a24b]/15 ${socio ? "bg-[#c9a24b]/[0.04]" : ""}`} data-testid={`vantagens-bilhete-${i}`}>
                                                <td className={`px-4 py-3 font-light ${socio ? "text-[#efe6d6]" : "text-[#efe6d6]/70"}`}>{l[0]}</td>
                                                <td className={`px-4 py-3 font-display text-lg whitespace-nowrap ${socio ? "gold-text font-medium" : "text-[#efe6d6]/80"}`}>{l[1]}</td>
                                                <td className={`px-4 py-3 font-display text-lg whitespace-nowrap ${socio ? "gold-text font-medium" : "text-[#efe6d6]/80"}`}>{l[2]}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <p className="font-light text-xs text-[#efe6d6]/50 leading-relaxed mt-4" data-testid="vantagens-bilhetes-nota">{v.bilhetesNota}</p>
                        <div className="mt-6 border-l border-[#c9a24b]/60 pl-5" data-testid="vantagens-fisico">
                            <p className="font-caps text-[10px] tracking-[0.28em] uppercase text-[#c9a24b]/80">{v.fisicoTitulo}</p>
                            <p className="font-light text-sm sm:text-base text-[#efe6d6]/75 leading-relaxed mt-2">{v.fisicoTexto}</p>
                        </div>
                    </section>
                )}

                <section className="mt-16" data-testid="vantagens-protocolos">
                    <Heading icon={Wine} testid="vantagens-protocolos-title">{v.protocolosTitulo}</Heading>
                    <div className="mt-6 space-y-5">
                        <Protocolo id="sr-vinho" logo={srVinho.logo} titulo={v.srVinhoTitulo} tag={v.srVinhoTag} texto={v.srVinhoTexto} cta={v.srVinhoCta} url={srVinho.url} testid="protocolo-sr-vinho" />
                        <Protocolo id="cr7-helder" logo={cr7.logo} titulo={v.cr7Titulo} tag={v.cr7Tag} texto={v.cr7Texto} codigo={v.cr7Codigo} cta={v.cr7Cta} url="https://www.vachoux.com/" testid="protocolo-cr7-helder" />
                        {comBilhetes && (
                            <Protocolo id="famosa" logo={famosa.logo} titulo={v.famosaTitulo} tag={v.famosaTag} texto={v.famosaTexto} testid="protocolo-famosa" />
                        )}
                    </div>
                    <p className="flex items-center gap-3 font-light text-sm text-[#efe6d6]/55 mt-6" data-testid="vantagens-em-breve">
                        <Store size={14} className="text-[#c9a24b]/70 shrink-0" strokeWidth={1.5} />{v.emBreve}
                    </p>
                </section>

                <section className="mt-16 border border-[#c9a24b]/30 bg-[#0a0705]/70 p-8 text-center" data-testid="vantagens-cta">
                    <p className="font-display font-medium text-2xl text-[#efe6d6]">{v.ctaTitulo}</p>
                    <p className="font-light text-sm text-[#efe6d6]/65 mt-2">{v.ctaTexto}</p>
                    <Link to="/associacao#adesao" data-testid="vantagens-cta-button"
                        className="mt-6 inline-flex items-center gap-2 font-caps text-[11px] tracking-[0.25em] uppercase text-[#070503] bg-[#c9a24b] px-8 py-3.5 hover:bg-[#ecd49b] transition-colors duration-400">
                        {v.cta}
                    </Link>
                </section>
            </motion.div>
        </div>
    );
}
